<template>
    <div>
        <h4 class="text-lightblue">
            {{ owner }}
            <b-button class="btn btn-success" href="/u/account/client-certificate/new">
                {{ $t('pages.client-certificate.new') }}
            </b-button>
        </h4>

        <div class="mb-4">
            {{ $t('pages.client-certificate.active_ones', {active: active}) }}
            {{ $t('pages.client-certificate.only_requested_here') }}
        </div>

        <b-table striped hover
                 :items="certificates"
                 :fields="fields"
                 :sort-by.sync="sortBy"
                 :sort-desc.sync="sortDesc"
                 sort-icon-left
                 responsive="sm"
                 v-if="hasCertificates"
        >
            <template #cell(state)="row">
                            <span class="badge" :class="{
                                'badge-success': (states.success.includes(row.item.state)),
                                'badge-danger': (states.danger.includes(row.item.state)),
                                'badge-primary':(states.primary.includes(row.item.state)),
                                'badge-dark': (states.dark.includes(row.item.state))
                             }">
                                {{ row.item.state }}
                            </span>
                <span v-if="row.item.key_deleted" class="badge badge-warning">closed</span>
            </template>

            <template #cell(actions)="row">
                <b-button class="btn btn-primary" size="sm" @click="row.toggleDetails">
                    {{
                        row.detailsShowing ? $t('pages.client-certificate.details.hide') : $t('pages.client-certificate.details.show')
                    }}
                </b-button>
                <b-button class="btn btn-success" size="sm" @click="download(row.item.ID, row.item.CCID)"
                          v-if="row.item.downloadable">
                    Download
                </b-button>
            </template>
            <template #row-details="row">
                <div v-html="row.item.email_exact">&nbsp;</div>
                <div v-html="row.item.details">&nbsp;</div>
            </template>
        </b-table>

        <div class="mt-4 mb-4">&nbsp;</div>

        <h5>{{ this.$t('pages.client-certificate.legend_title') }}</h5>

        <b-table striped hover
                 :items="legend"
                 :fields="legend_fields">

            <template #cell(state)="row">
                            <span class="badge" :class="{
                                'badge-success': (states.success.includes(row.item.state)),
                                'badge-danger': (states.danger.includes(row.item.state)),
                                'badge-primary':(states.primary.includes(row.item.state)),
                                'badge-dark': (states.dark.includes(row.item.state)),
                                'badge-warning': (states.warning.includes(row.item.state))
                             }">
                                {{ row.item.state }}
                            </span>
                <span v-if="row.item.key_deleted" class="badge badge-warning">closed</span>
            </template>
        </b-table>
    </div>
</template>

<script>
import GenericNotification from "../layout/GenericNotification";
import Callout from "../layout/Callout";
import PageCard from "../layout/PageCard";
import LoadingInjector from "../../mixins/LoadingInjector";
import purify from "dompurify";

export default {
    mixins: [
        LoadingInjector
    ],
    components: {
        PageCard,
        Callout,
        GenericNotification,
    },
    props: {
        rzaccount: {
            required: true,
            type: String,
        },
        downloadstub: {
            required: true,
            type: String,
        }
    },

    data() {
        return {
            certificates: [],
            owner: '',
            active: 0,
            sortBy: 'ID',
            sortDesc: true,
            fields: [
                {key: 'ID', sortable: true},
                {key: 'state', label: this.$t('pages.client-certificate.overview.columns.state'), sortable: false},
                {key: 'expires', label: this.$t('pages.client-certificate.overview.columns.expires'), sortable: true},
                {key: 'actions', label: this.$t('pages.client-certificate.overview.columns.actions'), sortable: false}
            ],
            states: {
                success: [
                    'issued',
                ],
                warning: [
                    'closed'
                ],
                primary: [
                    'requested',
                    'downloaded',
                ],
                danger: [
                    'expired',
                    'revoked'
                ],
                dark: [
                    'created',
                    'rejected',
                    'pre_revoked'
                ],
            },
            legend: [
                {"state": "issued", "description": this.$t('pages.client-certificate.legend.issued')},
                {"state": "closed", "description": this.$t('pages.client-certificate.legend.closed')},
                {"state": "requested", "description": this.$t('pages.client-certificate.legend.requested')},
                {"state": "downloaded", "description": this.$t('pages.client-certificate.legend.downloaded')},
                {"state": "expired", "description": this.$t('pages.client-certificate.legend.expired')},
                {"state": "revoked", "description": this.$t('pages.client-certificate.legend.revoked')},
                {"state": "created", "description": this.$t('pages.client-certificate.legend.created')},
                {"state": "rejected", "description": this.$t('pages.client-certificate.legend.rejected')},
            ],
            legend_fields: [
                {key: 'state', label: this.$t('pages.client-certificate.overview.columns.state')},
                {key: 'description', label: this.$t('pages.client-certificate.overview.columns.description')}
            ]
        }
    },
    created() {
        this.errorHandler = this.$shared.errorHandlerForResponder;
    },
    mounted() {
        console.log('Mounted ClientCertificate.');
        this.retrieve();
    },
    methods: {
        retrieve: function () {
            this.$http.get(this.$Bologna.secure_url(`/v1/client-certificate/retrieve/${this.rzaccount}`))
                .then(response => {
                    let data = Object.values(response.data.data.certificates);
                    this.owner = this.$purify.sanitize(response.data.data.for);
                    this.active = this.$purify.sanitize(response.data.data.active);
                    let certificates = [];
                    data.forEach(function (value, key) {
                        if (!value.sectigo.hasOwnProperty('backendCertId')) {
                            return;
                        }
                        certificates.push({
                            ID: purify.sanitize(value.sectigo.backendCertId),
                            CCID: purify.sanitize(value.ccid),
                            state: purify.sanitize(value.sectigo.state),
                            details: purify.sanitize(`<p class="text-bold">${value.sectigo.serialNumber}</p><p class="text-muted">${value.sectigo.certificateDetails.subject}</p>`),
                            email_exact: purify.sanitize(value.email_exact),
                            expires: purify.sanitize(value.sectigo.expires),
                            downloadable: (value.sectigo.state === "issued" && value.key_deleted_at == null),
                            key_deleted: value.key_deleted_at !== null,
                        });
                    })
                    this.certificates = certificates;
                })
                .catch(error => {

                })
                .finally(() => {
                    this.loading = false;
                });
        },
        download: function (orderNumber, ccid) {
            console.log(`DOWNLOAD ${orderNumber}`);

            this.$http.get(this.$Bologna.secure_url(`/v1/client-certificate/transfer/${this.rzaccount}/${orderNumber}/${ccid}`))
                .then(response => {
                    const blob = new Blob([Buffer.from(response.data.data.attachment, 'base64')], {type: 'application/octet-stream'});
                    const link = document.createElement('a');
                    link.href = URL.createObjectURL(blob);
                    link.download = purify.sanitize(response.data.data.filename);
                    link.click();
                    URL.revokeObjectURL(link.href);
                });

            return true;
        },
    },
    computed: {
        hasCertificates: function () {
            return this.certificates.length > 0;
        }
    }
}
</script>
